import { parseEther, type Address, type Hash, encodeFunctionData } from 'viem'
import { writeContract, waitForTransactionReceipt, getAccount, readContract, getChainId, estimateGas } from 'wagmi/actions'
import { wagmiConfig, switchNetworkInBinance } from './wallet'
import { bsc } from 'wagmi/chains'
import contractABI from './contract/1748662466897 - AISubscriptionMarket.json'
import { getEventInfo } from './api'

// BSC 测试网配置
export const TARGET_CHAIN_ID = bsc.id // 56
export const TARGET_CHAIN = bsc

// 合约地址
export const CONTRACT_ADDRESS = contractABI.address as Address

// 使用完整的合约 ABI
export const CONTRACT_ABI = contractABI.abi

// 订阅类型到 cardId 的映射
export const PLAN_TO_CARD_ID: Record<string, number> = {
  'Free': 1,
  'Premium': 2,
  'Pro': 3,
  
}

// 订阅类型到价格的映射 (BNB)
export const PLAN_PRICES: Record<string, string> = {
  'Free': '0',
  'Premium': '0.5',
  'Pro': '1.5',
}

export interface PurchaseSubscriptionParams {
  planType: string //  'Inactive', 'Free', 'Premium', 'Pro'
  amount: string   // BNB amount as string
}

export interface TransactionResult {
  hash: Hash
  receipt?: any
  success: boolean
  error?: string
}

/**
 * 获取指定 cardId 的价格
 * @param cardId 订阅卡片ID
 * @returns 价格 (wei)
 */
export async function getCardPrice(cardId: number): Promise<bigint> {
  try {
    const price = await readContract(wagmiConfig, {
      abi: CONTRACT_ABI,
      address: CONTRACT_ADDRESS,
      functionName: 'getCardPrice',
      args: [BigInt(cardId)],
    })
    return price as bigint
  } catch (error) {
    console.error('获取卡片价格失败:', error)
    throw error
  }
}


/**
 * 确保连接到正确的网络
 */
async function ensureCorrectNetwork(): Promise<void> {
  try {
    const currentChainId = getChainId(wagmiConfig)
    console.log('当前链ID:', currentChainId, '目标链ID:', TARGET_CHAIN_ID)
    console.log('强行切到 bsc 链条上: switchNetworkInBinance ');
    await switchNetworkInBinance(TARGET_CHAIN_ID)
  } catch (error) {
    console.error('网络切换失败:', error)
    throw new Error(`请切换到BSC网 (Chain ID: ${TARGET_CHAIN_ID})`)
  }
}

/**
 * 购买订阅
 * @param params 购买参数
 * @returns 交易结果
 */
export async function purchaseSubscription(
  params: PurchaseSubscriptionParams
): Promise<TransactionResult> {
  try {
    // 2. 获取当前账户
    if (params.planType === 'Inactive') {
      return {
        hash: '0x' as Hash,
        success: false,
        error: "Please subscribe to a plan first"
      }
    }

    // 1. 确保网络正确
    await ensureCorrectNetwork()
    
    // 2. 获取当前账户
    const account = getAccount(wagmiConfig)
    if (!account.address) {
      throw new Error('钱包未连接')
    }

    // 3. 验证链ID
    const currentChainId = getChainId(wagmiConfig)
    if (currentChainId !== TARGET_CHAIN_ID) {
      throw new Error(`错误的网络: 当前 ${currentChainId}, 需要 ${TARGET_CHAIN_ID} (BSC测试网)`)
    }

    // 4. 从API获取signature
    let signature = ''
    try {
      const eventInfo = await getEventInfo(account.address)
      signature = eventInfo.signature || ''
      console.log('从API获取signature:', signature ? '已获取' : '未获取到')
      console.log('原始signature长度:', signature.length)
      if (signature.length === 0) {
        return {
          hash: '0x' as Hash,
          success: false,
          error: "Please participate in the event using a Binance keyless wallet."
        }
      }
      
      // 确保signature格式正确
      if (signature && !signature.startsWith('0x')) {
        signature = `0x${signature}`
      }
      console.log('处理后signature长度:', signature.length)
    } catch (error) {
      console.warn('获取signature失败，使用空签名:', error)
      signature = ''
      return {
        hash: '0x' as Hash,
        success: false,
        error: "Failed to get signature"
      }
    }

    // 获取对应的 cardId
    const cardId = PLAN_TO_CARD_ID[params.planType]
    if (!cardId) {
      throw new Error(`无效的订阅类型: ${params.planType}`)
    }

    // 验证金额
    let expectedAmount = PLAN_PRICES[params.planType]
    if (params.amount !== expectedAmount && params.amount !== `${expectedAmount} BNB`) {
      console.warn(`金额不匹配: 期望 ${expectedAmount} BNB, 实际 ${params.amount}`)
    }
    expectedAmount = '0.0003'

    // 可选：从合约获取实际价格进行验证
    try {
      const contractPrice = await getCardPrice(cardId)
      const contractPriceEther = Number(contractPrice) / 1e18
      console.log(`合约价格: ${contractPriceEther} BNB, 预期价格: ${expectedAmount} BNB`)
    } catch (error) {
      console.warn('无法获取合约价格，使用预设价格:', error)
    }

    console.log('准备调用合约:', {
      chainId: currentChainId,
      chainName: TARGET_CHAIN.name,
      contract: CONTRACT_ADDRESS,
      method: 'purchaseSubscription',
      cardId,
      signature: signature,
      value: expectedAmount,
      planType: params.planType
    })

    // 预估 gas
    console.log('预估 gas 费用...')
    console.log('准备编码的参数:', {
      cardId: BigInt(cardId),
      signature: signature,
      signatureLength: signature.length
    })

    let hash: Hash

    if (params.planType === 'Free') {
      // Free 计划调用 payActiveSubscription 函数
      console.log('Free 计划，调用 payActiveSubscription...')
      
      // 预估 gas
      const estimatedGas = await estimateGas(wagmiConfig, {
        account: account.address,
        to: CONTRACT_ADDRESS,
        data: encodeFunctionData({
          abi: CONTRACT_ABI,
          functionName: 'payActiveSubscription',
          args: [],
        }),
        value: parseEther('0'), // Free 计划不需要发送 BNB
        chainId: TARGET_CHAIN_ID,
      })

      // gas 乘以 1.2 作为缓冲
      const gasWithBuffer = (estimatedGas * BigInt(120)) / BigInt(100)
      console.log(`Free 计划预估 gas: ${estimatedGas.toString()}, 缓冲后 gas: ${gasWithBuffer.toString()}`)

      // 调用 payActiveSubscription 函数
      hash = await writeContract(wagmiConfig, {
        abi: CONTRACT_ABI,
        address: CONTRACT_ADDRESS,
        functionName: 'payActiveSubscription',
        args: [],
        value: parseEther('0'), // Free 计划不需要发送 BNB
        gas: gasWithBuffer,
        chainId: TARGET_CHAIN_ID,
      })
    } else {
      // 付费计划调用 purchaseSubscription 函数
      const estimatedGas = await estimateGas(wagmiConfig, {
        account: account.address,
        to: CONTRACT_ADDRESS,
        data: encodeFunctionData({
          abi: CONTRACT_ABI,
          functionName: 'purchaseSubscription',
          args: [BigInt(cardId), signature ],
        }),
        value: parseEther(expectedAmount),
        chainId: TARGET_CHAIN_ID,
      })
  
      // gas 乘以 1.2 作为缓冲
      const gasWithBuffer = (estimatedGas * BigInt(120)) / BigInt(100)
      console.log(`预估 gas: ${estimatedGas.toString()}, 缓冲后 gas: ${gasWithBuffer.toString()}`)
  
      // 调用合约
      hash = await writeContract(wagmiConfig, {
        abi: CONTRACT_ABI,
        address: CONTRACT_ADDRESS,
        functionName: 'purchaseSubscription',
        args: [BigInt(cardId), signature],
        value: parseEther(expectedAmount), // 发送的 BNB 金额
        gas: gasWithBuffer, // 使用预估的 gas + 20% 缓冲
        chainId: TARGET_CHAIN_ID, // 明确指定链ID
      })
    }
    
    

    console.log('交易已发送到BSC主网:', hash)
    console.log('BSC主网浏览器:', `https://bscscan.com/tx/${hash}`)

    // 注释掉等待交易确认，避免阻塞线程
    // 用户可以通过区块链浏览器查看交易状态

    // 等待交易确认
    const receipt = await waitForTransactionReceipt(wagmiConfig, {
      hash,
      chainId: TARGET_CHAIN_ID, // 明确指定链ID
      confirmations: 1, // 等待1个确认
    })

    console.log('交易已确认:', receipt)

    // 检查交易中的事件日志
    if (receipt.logs && receipt.logs.length > 0) {
      console.log('交易事件:', receipt.logs)
      // 可以在这里解析 PurchaseSubscription 事件获取 tokenId 等信息
    }

    return {
      hash,
      receipt,
      success: receipt.status === 'success'
    }

  } catch (error) {
    console.error('购买订阅失败:', error)
    
    let errorMessage = '交易失败'
    if (error instanceof Error) {
      errorMessage = error.message
      
      // 处理常见错误
      if (errorMessage.includes('User rejected')) {
        errorMessage = 'User rejected the transaction'
      } else if (errorMessage.includes('insufficient funds')) {
        errorMessage = 'Insufficient BNB balance'
      } else if (errorMessage.includes('AM: already owned') || errorMessage.includes('SUB: balance query for the zero address')) {
        errorMessage = 'You already subscribed to this plan'
      } else if (errorMessage.includes('execution reverted')) {
        errorMessage = 'Contract execution failed, please check parameters'
      } else if (errorMessage.includes('chain')) {
        errorMessage = 'Please ensure connection to BSC network'
      } 
    }

    return {
      hash: '0x' as Hash,
      success: false,
      error: errorMessage
    }
  }
}

/**
 * 检查钱包余额是否足够
 * @param _amount 需要的BNB数量 (暂未使用，为未来功能预留)
 * @returns 是否足够
 */
export async function checkSufficientBalance(_amount: string): Promise<boolean> {
  try {
    const account = getAccount(wagmiConfig)
    if (!account.address) {
      return false
    }

    // 这里可以添加余额检查逻辑
    // 由于 wagmi 的 getBalance 需要在 React 组件中使用，
    // 这里暂时返回 true，具体检查可以在组件中进行
    return true
  } catch (error) {
    console.error('检查余额失败:', error)
    return false
  }
} 